import { DashboardConfig } from '../types/dashboard';

/**
 * Collection of dashboard configurations for each group
 * (should map to MSAL groups)
 * 'Skyway Range',
  'DronePort Network',
  'Atlas',
  'Asemio',
  'TCC',
  'THEC',
  'TU - ASWP',
  'inTulsa - ASWP',
  'OSU',
  'TU - ARCH',
  'inTulsa - CNTRL',
  'Black Tech Street',
  'TU - SAFE-T'
 */
const performanceDashboardConfigs: Record<string, DashboardConfig> = {
  'Skyway Range': {
    id: 'skyway-dashboard',
    name: 'Skyway Range Performance Dashboard',
    description: 'Key metrics for tracking Skyway Range performance',
    canViewExternal: ['DronePort Network'], // Groups that can view this dashboard
    sectionGroupings: [
      {
        name: "Client Acquisition & Revenue",
        layout: {
          columns: 1,
          charts: [
            {
              id: 'client-acquisition-trends',
              title: 'Client Acquisition & Engagement Metrics',
              description: 'Tracking progress against annual targets',
              type: 'multiline',
              dataKey: 'clientAcquisitions',
              xAxis: {
                dataKey: 'quarter',
                type: 'category',
              },
              yAxis: {
                type: 'number',
                label: 'Number of clients',
                domain: ['auto', 'auto'],
                tickCount: 5,
              },
              series: [
                {
                  dataKey: 'acquisitions',
                  name: 'Client Acquisitions',
                  stroke: '#8884d8',
                  dot: { r: 4 },
                  activeDot: { r: 6 },
                },
                {
                  dataKey: 'engagements',
                  name: 'Client Engagements',
                  stroke: '#82ca9d',
                  dot: { r: 4 },
                  activeDot: { r: 6 },
                },
                {
                  dataKey: 'internationalTesting',
                  name: 'International Testing',
                  stroke: '#ffc658',
                  dot: { r: 4 },
                  activeDot: { r: 6 },
                },
                {
                  dataKey: 'annualTargetEngagements',
                  name: 'Target Engagements',
                  stroke: '#82ca9d',
                  strokeDasharray: '5 5',
                  dot: false,
                },
                {
                  dataKey: 'annualTargetAcquisitions',
                  name: 'Target Acquisitions',
                  stroke: '#8884d8',
                  strokeDasharray: '5 5',
                  dot: false,
                }
              ],
              legend: {
                verticalAlign: 'bottom',
                align: 'center',
                layout: 'horizontal',
                iconType: 'line'
              },
              height: 350,
              dataBoxes: [
                {
                  label: 'Engagements Ratio',
                  value: '5:2',
                  descriptor: 'Q3 2025',
                  tooltip: 'Number of active client engagements to acquisitions as of this quarter',
                  color: '#8884d8'
                }
              ]

            },
            {
              id: 'revenue-by-quarter',
              title: 'Quarterly Revenue',
              description: 'Revenue from acquired clients by quarter',
              type: 'stackedBar',
              dataKey: 'quarterlyRevenue',
              xAxis: {
                dataKey: 'quarter',
                type: 'category',
              },
              yAxis: {
                label: 'Revenue',
                domain: ['auto', 'auto'],
                tickFormatter: 'formatCurrencyShortTooltip'
              },
              series: [
                {
                  dataKey: 'currentQuarterAcquisitionRevenue',
                  name: 'Acquired This Quarter',
                  fill: '#8884d8',
                  stackId: 'revenue'
                },
                {
                  dataKey: 'priorQuartersAcquisitionRevenue',
                  name: 'Acquired Previous Quarters',
                  fill: '#82ca9d',
                  stackId: 'revenue'
                }
              ],
              legend: {
                verticalAlign: 'bottom',
                align: 'center',
              },
              tooltip: {
                formatter: 'formatCurrencyShortTooltip'
              },
              height: 350,
              dataBoxes: [
                {
                  label: '2029 Target',
                  value: '$500,000',
                  descriptor: 'Annual Revenue',
                  tooltip: '$500k/yr target by 2029',
                  color: '#4CAF50'
                },
                {
                  label: 'QoQ Growth',
                  value: '23.9%',
                  descriptor: 'Compared to Q1 2025',
                  tooltip: 'Quarter-over-quarter revenue growth percentage',
                  color: '#2196F3'
                }
              ]
            },
          ]
        }
      },
      // {
      //   name: "Marketing and Business Development",
      //   layout: {
      //     columns: 2,
      //     charts: [
      //       {
      //         id: 'economic-impact-placeholder',
      //         title: 'Placeholder: Tulsa Impact',
      //         description: 'Marketing/BD vs Acquisition',
      //         type: 'placeholder',
      //         dataKey: 'economicImpact', // This doesn't need to exist in the data
      //         placeholderMessage: 'Marketing/business development activities vs client acquisition data will be available in Q4 2025',
      //         height: 300,
      //         series: []
      //       }
      //     ]
      //   },
      // },
      {
        name: "Serving Tulsa Dashboard Series",
        layout: {
          columns: 2,
          charts: [
            {
              id: 'tulsa-comparison-dollars',
              title: 'Tulsa MSA vs Outside MSA Dollars',
              description: 'Revenue comparison: clients that assemble/manufacture in Tulsa MSA vs outside MSA',
              type: 'pie',
              dataKey: 'tulsaProducerRevenue',
              series: [
                {
                  dataKey: 'value',
                  name: 'Revenue',
                  fill: '#8884d8',
                }
              ],
              legend: {
                verticalAlign: 'bottom',
                align: 'center'
              },
              tooltip: {
                formatter: 'formatCurrencyTooltip'
              },
              trendInfo: true,
              height: 300,
              dataBoxes: [{
                label: 'QoQ Change',
                value: '12%',
                descriptor: '12% more in Tulsa Since Previous Quarter',
                color: '#ffc658ff'
              }],
            },
            {
              id: 'tulsa-producer-dollars',
              title: 'Dollars from Tulsa MSA Producers',
              description: 'Proportion of revenue from clients that assemble/manufacture in Tulsa MSA',
              type: 'pie',
              dataKey: 'tulsaMSAProducerRevenue',
              series: [
                {
                  dataKey: 'value',
                  name: 'Revenue',
                  fill: '#8884d8',
                }
              ],
              legend: {
                verticalAlign: 'bottom',
                align: 'center'
              },
              tooltip: {
                formatter: 'formatCurrencyTooltip'
              },
              trendInfo: true,
              height: 300,
              dataBoxes: [
                {
                  label: 'QoQ Change',
                  value: '7.3%',
                  descriptor: '7.3% more in Tulsa Since Previous Quarter',
                  color: '#4CAF50'
                }
              ],
            },
            {
              id: 'tulsa-msa-employment',
              title: 'Employment: clients that employ workers in Tulsa MSA',
              description: 'Proportion of clients that employ workers in Tulsa MSA ',
              type: 'pie',
              dataKey: 'tulsaEmployment',
              series: [
                {
                  dataKey: 'value',
                  name: 'Revenue',
                  fill: '#8884d8',
                }
              ],
              legend: {
                verticalAlign: 'bottom',
                align: 'center'
              },
              tooltip: {
                formatter: 'formatPercentTooltip'
              },
              trendInfo: true,
              height: 300,
              dataBoxes: [
                {
                  label: 'QoQ Change',
                  value: '1.4%',
                  descriptor: '1.4% more in Tulsa Since Previous Quarter',
                  color: '#8884d8'
                }
              ],
            },
            {
              id: 'risk-assessment',
              title: 'Risk Assessment',
              description: 'Quarterly risk assessment',
              type: 'line',
              dataKey: 'riskAssessment',
              xAxis: {
                dataKey: 'quarter',
                type: 'category',
              },
              yAxis: {
                dataKey: 'nodes',
                label: 'Nodes',
              },
              series: [
                {
                  dataKey: 'nodes',
                  name: 'Nodes',
                  stroke: '#82ca9d',
                  dot: { r: 4 },
                  activeDot: { r: 6 },
                }
              ],
              height: 300,
            },
          ]
        }
      },
    ],
  },
  'DronePort Network': {
    id: 'droneport-dashboard',
    name: 'DronePort Network Performance Dashboard',
    description: 'Performance metrics for DronePort Network',
    canViewExternal: ['Skyway Range'],
    sectionGroupings: [
      {
        name: "Client Acquisition & Revenue",
        layout: {
          columns: 1,
          charts: [
            {
              id: 'client-acquisition-trends',
              title: 'Client Acquisition & Engagement Metrics',
              description: 'Tracking progress against annual targets',
              type: 'multiline',
              dataKey: 'clientAcquisitions',
              xAxis: {
                dataKey: 'quarter',
                type: 'category',
              },
              yAxis: {
                type: 'number',
                label: 'Number of clients',
                domain: ['auto', 'auto'],
                tickCount: 5,
              },
              series: [
                {
                  dataKey: 'acquisitions',
                  name: 'Client Acquisitions',
                  stroke: '#8884d8',
                  dot: { r: 4 },
                  activeDot: { r: 6 },
                },
                {
                  dataKey: 'engagements',
                  name: 'Client Engagements',
                  stroke: '#82ca9d',
                  dot: { r: 4 },
                  activeDot: { r: 6 },
                },
                {
                  dataKey: 'internationalTesting',
                  name: 'International Testing',
                  stroke: '#ffc658',
                  dot: { r: 4 },
                  activeDot: { r: 6 },
                },
                {
                  dataKey: 'annualTargetEngagements',
                  name: 'Target Engagements',
                  stroke: '#82ca9d',
                  strokeDasharray: '5 5',
                  dot: false,
                },
                {
                  dataKey: 'annualTargetAcquisitions',
                  name: 'Target Acquisitions',
                  stroke: '#8884d8',
                  strokeDasharray: '5 5',
                  dot: false,
                }
              ],
              legend: {
                verticalAlign: 'bottom',
                align: 'center',
                layout: 'horizontal',
                iconType: 'line'
              },
              height: 350,
              dataBoxes: [
                {
                  label: 'Engagements Ratio',
                  value: '5:2',
                  descriptor: 'Q3 2025',
                  tooltip: 'Number of active client engagements to acquisitions as of this quarter',
                  color: '#8884d8'
                }
              ]

            },
            {
              id: 'revenue-by-quarter',
              title: 'Quarterly Revenue',
              description: 'Revenue from acquired clients by quarter',
              type: 'stackedBar',
              dataKey: 'quarterlyRevenue',
              xAxis: {
                dataKey: 'quarter',
                type: 'category',
              },
              yAxis: {
                label: 'Revenue',
                domain: ['auto', 'auto'],
              },
              series: [
                {
                  dataKey: 'currentQuarterAcquisitionRevenue',
                  name: 'Acquired This Quarter',
                  fill: '#8884d8',
                  stackId: 'revenue'
                },
                {
                  dataKey: 'priorQuartersAcquisitionRevenue',
                  name: 'Acquired Previous Quarters',
                  fill: '#82ca9d',
                  stackId: 'revenue'
                }
              ],
              legend: {
                verticalAlign: 'bottom',
                align: 'center',
              },
              height: 350,
              dataBoxes: [
                {
                  label: '2029 Target',
                  value: '$500,000',
                  descriptor: 'Annual Revenue',
                  tooltip: '$500k/yr target by 2029',
                  color: '#4CAF50'
                },
                {
                  label: 'QoQ Growth',
                  value: '23.9%',
                  descriptor: 'Compared to Q1 2025',
                  tooltip: 'Quarter-over-quarter revenue growth percentage',
                  color: '#2196F3'
                }
              ]
            },
          ]
        }
      },
      {
        name: "Marketing and Business Development",
        layout: {
          columns: 2,
          charts: [
            {
              id: 'economic-impact-placeholder',
              title: 'Placeholder: Tulsa Impact',
              description: 'Marketing/BD vs Acquisition',
              type: 'placeholder',
              dataKey: 'economicImpact', // This doesn't need to exist in the data
              placeholderMessage: 'Marketing/business development activities vs client acquisition data will be available in Q4 2025',
              height: 300,
              series: []
            }
          ]
        },
      },
      {
        name: "Serving Tulsa Dashboard Series",
        layout: {
          columns: 2,
          charts: [
            {
              id: 'tulsa-comparison-dollars',
              title: 'Tulsa MSA vs Outside MSA Dollars',
              description: 'Revenue comparison: clients that assemble/manufacture in Tulsa MSA vs outside MSA',
              type: 'pie',
              dataKey: 'tulsaProducerRevenue',
              series: [
                {
                  dataKey: 'value',
                  name: 'Revenue',
                  fill: '#8884d8',
                }
              ],
              legend: {
                verticalAlign: 'bottom',
                align: 'center'
              },
              tooltip: {
                formatter: 'formatCurrencyTooltip'
              },
              trendInfo: true,
              height: 300,
              dataBoxes: [{
                label: 'QoQ Change',
                value: '12%',
                descriptor: '12% more in Tulsa Since Previous Quarter',
                color: '#ffc658ff'
              }],
            },
            {
              id: 'tulsa-producer-dollars',
              title: 'Dollars from Tulsa MSA Producers',
              description: 'Proportion of revenue from clients that assemble/manufacture in Tulsa MSA',
              type: 'pie',
              dataKey: 'tulsaMSAProducerRevenue',
              series: [
                {
                  dataKey: 'value',
                  name: 'Revenue',
                  fill: '#8884d8',
                }
              ],
              legend: {
                verticalAlign: 'bottom',
                align: 'center'
              },
              tooltip: {
                formatter: 'formatCurrencyTooltip'
              },
              trendInfo: true,
              height: 300,
              dataBoxes: [
                {
                  label: 'QoQ Change',
                  value: '7.3%',
                  descriptor: '7.3% more in Tulsa Since Previous Quarter',
                  color: '#4CAF50'
                }
              ],
            },
            {
              id: 'tulsa-msa-employment',
              title: 'Employment: clients that employ workers in Tulsa MSA',
              description: 'Proportion of clients that employ workers in Tulsa MSA ',
              type: 'pie',
              dataKey: 'tulsaEmployment',
              series: [
                {
                  dataKey: 'value',
                  name: 'Revenue',
                  fill: '#8884d8',
                }
              ],
              legend: {
                verticalAlign: 'bottom',
                align: 'center'
              },
              tooltip: {
                formatter: 'formatPercentTooltip'
              },
              trendInfo: true,
              height: 300,
              dataBoxes: [
                {
                  label: 'QoQ Change',
                  value: '1.4%',
                  descriptor: '1.4% more in Tulsa Since Previous Quarter',
                  color: '#8884d8'
                }
              ],
            },
            {
              id: 'risk-assessment',
              title: 'Risk Assessment',
              description: 'Quarterly risk assessment',
              type: 'line',
              dataKey: 'riskAssessment',
              xAxis: {
                dataKey: 'quarter',
                type: 'category',
              },
              yAxis: {
                dataKey: 'nodes',
                label: 'Nodes',
              },
              series: [
                {
                  dataKey: 'nodes',
                  name: 'Nodes',
                  stroke: '#82ca9d',
                  dot: { r: 4 },
                  activeDot: { r: 6 },
                }
              ],
              height: 300,
            },
          ]
        }
      },
    ],
  },
  'TU - SAFE-T': {
    id: 'tu-safe-t-dashboard',
    name: 'SAFE-T Performance Dashboard',
    description: 'Key metrics for tracking TU SAFE-T performance',
    canViewExternal: [],
    sectionGroupings: [
      {
        name: "Applied Security Controls",
        layout: {
          columns: 1,
          charts: [
            {
              id: 'tu-applied-security-chart',
              title: 'Security Controls',
              description: 'Tracking nodes with applied security controls',
              type: 'multiline',
              dataKey: 'appliedSecurityControls',
              xAxis: {
                dataKey: 'quarter',
                type: 'category',
                // Add custom rendering for x-axis ticks to show total beneath quarter
                tickFormatter: 'formatQuarterWithTotal'
              },
              yAxis: {
                type: 'number',
                label: 'Nodes',
                domain: ['auto', 'auto'],
                tickCount: 5,
              },
              series: [
                {
                  dataKey: 'control1Nodes',
                  name: 'STEM Lab CNC Router',
                  dot: { r: 4 },
                  activeDot: { r: 6 },
                }, {
                  dataKey: 'control2Nodes',
                  name: 'Cloud Backup',
                  dot: { r: 4 },
                  activeDot: { r: 6 },
                }, {
                  dataKey: 'control3Nodes',
                  name: 'Health Facility',
                  dot: { r: 4 },
                  activeDot: { r: 6 },
                }, {
                  dataKey: 'control4Nodes',
                  name: 'Cafeteria Fume Hood',
                  dot: { r: 4 },
                  activeDot: { r: 6 },
                },
              ],
              legend: {
                verticalAlign: 'bottom',
                align: 'center',
                layout: 'horizontal',
                iconType: 'line'
              },
              height: 350,
              dataBoxes: [
                {
                  label: 'Total Controls',
                  value: '17',
                  descriptor: 'Q3 2025',
                }
              ],
            },
          ]
        }
      },
      {
        name: "Node Risk Assessment",
        layout: {
          columns: 3,
          charts: [
            {
              id: 'tu-risk-distribution-chart',
              title: 'Risk Distribution',
              description: 'Risk distribution across nodes',
              type: 'pie',
              dataKey: 'riskDistribution',
              height: 300,
              series: [
                {
                  dataKey: 'value',
                  name: 'Risk'
                }
              ],
              legend: {
                verticalAlign: 'bottom',
                align: 'center'
              },
              tooltip: {
                formatter: 'formatPercentTooltip'
              },
              dataBoxes: [
                {
                  label: 'High Risk',
                  value: '20',
                  descriptor: '4% of nodes',
                  color: '#cac382ff'
                }
              ],
            },
            {
              id: 'tu-risk-trendline-chart',
              title: 'Risk Trendline',
              description: 'Risk over time',
              type: 'multiline',
              dataKey: 'riskTrendline',
              xAxis: {
                dataKey: 'quarter',
                type: 'category',
              },
              yAxis: {
                type: 'number',
                label: 'Nodes',
                domain: ['auto', 'auto'],
                tickCount: 5,
              },
              series: [
                {
                  dataKey: 'lowRisk',
                  name: 'Low Risk',
                  stroke: '#82ca9d',
                  dot: { r: 4 },
                  activeDot: { r: 6 },
                }, {
                  dataKey: 'mediumRisk',
                  name: 'Medium Risk',
                  stroke: '#cac382ff',
                  dot: { r: 4 },
                  activeDot: { r: 6 },
                }, {
                  dataKey: 'highRisk',
                  name: 'High Risk',
                  stroke: '#cd545eff',
                  dot: { r: 4 },
                  activeDot: { r: 6 },
                }
              ],
              legend: {
                verticalAlign: 'bottom',
                align: 'center',
                layout: 'horizontal',
                iconType: 'line'
              },
              height: 350,
            },

            {
              id: 'tu-risk-threshold-meter',
              title: 'Risk Threshold',
              description: 'Security controls that are high or medium risk',
              type: 'threshold',
              dataKey: 'riskThreshold',
              series: [
                {
                  dataKey: 'value',
                  name: 'High & Medium Risk',
                }
              ],
              height: 200,
              threshold: {
                threshold: 20,
                annotation: 'Goal: No more than 20% of applied security controls in high or medium risk categories',
                belowThresholdColor: '#4CAF50', // Green
                aboveThresholdColor: '#e2554bff', // Red
                thresholdLineColor: '#8c170eff', // Orange
              }
            },
          ]
        },
      },
      {
        name: "Plan of Action and Milestones",
        layout: {
          columns: 1,
          charts: [
            {
              id: 'tu-stacked-poam-chart',
              title: 'Unmitigated POAMs',
              description: 'Medium & High Risk POAMs over time',
              type: 'stackedBar',
              dataKey: 'unmitigatedPoams',
              xAxis: {
                dataKey: 'quarter',
                type: 'category',
              },
              yAxis: {
                label: 'Med/High Risk Nodes',
                domain: ['auto', 'auto'],
              },
              series: [
                {
                  dataKey: 'without',
                  name: 'Nodes without POAMs',
                  stackId: 'riskNodes'
                },
                {
                  dataKey: 'undeveloped',
                  name: 'Nodes with undeveloped POAMs',
                  stackId: 'riskNodes'
                },

                {
                  dataKey: 'developedUnmonitored',
                  name: 'Nodes with developed unmonitored POAMs',
                  stackId: 'riskNodes'
                },
                {
                  dataKey: 'monitoredUnshared',
                  name: 'Nodes with monitored unshared POAMs',
                  stackId: 'riskNodes'
                },
                {
                  dataKey: 'shared',
                  name: 'Nodes with shared POAMs',
                  stackId: 'riskNodes'
                },
              ],
              legend: {
                verticalAlign: 'bottom',
                align: 'center',
              },
              tooltip: {
                formatter: 'none'
              },
              height: 350,
            },
          ]
        }
      },
    ],
  },
  'TU - ASWP': {
    id: 'tu-aswp-dashboard',
    name: 'ASWP Performance Dashboard',
    description: 'Key metrics for tracking TU ASWP performance',
    canViewExternal: [],
    sectionGroupings: [
      {
        name: "Primary Goals",
        layout: {
          columns: 1,
          charts: [
            {
              id: 'tu-awsp-minors-declared',
              title: 'Minors Declared',
              description: 'Tracking unique students declaring the AI minor',
              type: 'line',
              dataKey: 'tuAwspMinorsDeclared',
              xAxis: {
                dataKey: 'quarter',
                type: 'category',
              },
              yAxis: {
                dataKey: 'minors',
                label: 'AI Minors',
              },
              series: [
                {
                  dataKey: 'minors',
                  name: 'AI Minors',
                  stroke: '#82ca9d',
                  dot: { r: 4 },
                  activeDot: { r: 6 },
                }
              ],
              height: 300,
            },
            {
              id: 'tu-awsp-individuals-graduated',
              title: 'Individuals Graduated',
              description: 'Tracking unique students graduating from the AI minor',
              type: 'line',
              dataKey: 'tuAwspIndividualsGraduated',
              xAxis: {
                dataKey: 'quarter',
                type: 'category',
              },
              yAxis: {
                dataKey: 'graduates',
                label: 'Graduates',
              },
              series: [
                {
                  dataKey: 'graduates',
                  name: 'Graduates',
                  stroke: '#82ca9d',
                  dot: { r: 4 },
                  activeDot: { r: 6 },
                }
              ],
              height: 300,
            },
            {
              id: 'tu-awsp-hs-students-engaged',
              title: 'High School Students Engaged',
              description: 'Tracking unique high school students engaged in the AI minor',
              type: 'line',
              dataKey: 'tuAwspHsStudentsEngaged',
              xAxis: {
                dataKey: 'quarter',
                type: 'category',
              },
              yAxis: {
                dataKey: 'students',
                label: 'HS Students',
              },
              series: [
                {
                  dataKey: 'students',
                  name: 'HS Students',
                  stroke: '#82ca9d',
                  dot: { r: 4 },
                  activeDot: { r: 6 },
                }
              ],
              height: 300,
            },
          ]
        }
      },
      {
        name: "Secondary Goals",
        layout: {
          columns: 2,
          charts: [
            {
              id: 'tu-awsp-teachers-trained',
              title: 'Teachers Trained',
              description: 'Tracking unique teachers trained in AI',
              type: 'line',
              dataKey: 'tuAwspTeachersTrained',
              xAxis: {
                dataKey: 'quarter',
                type: 'category',
              },
              yAxis: {
                dataKey: 'teachers',
                label: 'Teachers',
              },
              series: [
                {
                  dataKey: 'teachers',
                  name: 'Teachers',
                  stroke: '#82ca9d',
                  dot: { r: 4 },
                  activeDot: { r: 6 },
                }
              ],
              height: 300,
            },
            {
              id: 'tu-awsp-participation-by-race',
              title: 'Participation by Race',
              description: 'Tracking participation in AI programs by race',
              type: 'line',
              dataKey: 'tuAwspParticipationByRace',
              xAxis: {
                dataKey: 'quarter',
                type: 'category',
              },
              yAxis: {
                dataKey: 'participants',
                label: 'Participants',
              },
              series: [
                {
                  dataKey: 'participants',
                  name: 'Participants',
                  stroke: '#82ca9d',
                  dot: { r: 4 },
                  activeDot: { r: 6 },
                }
              ],
              height: 300,
            },
            {
              id: 'tu-awsp-racial-completion',
              title: 'Completion by Race',
              description: 'Tracking racial component of completion',
              type: 'line',
              dataKey: 'tuAwspRacialCompletion',
              xAxis: {
                dataKey: 'quarter',
                type: 'category',
              },
              yAxis: {
                dataKey: 'participants',
                label: 'Participants',
              },
              series: [
                {
                  dataKey: 'participants',
                  name: 'Participants',
                  stroke: '#82ca9d',
                  dot: { r: 4 },
                  activeDot: { r: 6 },
                }
              ],
              height: 300,
            },
            {
              id: 'tu-awsp-gender-participation',
              title: 'Participation by Gender',
              description: 'Tracking gender participation',
              type: 'line',
              dataKey: 'tuAwspGenderParticipation',
              xAxis: {
                dataKey: 'quarter',
                type: 'category',
              },
              yAxis: {
                dataKey: 'participants',
                label: 'Participants',
              },
              series: [
                {
                  dataKey: 'participants',
                  name: 'Participants',
                  stroke: '#82ca9d',
                  dot: { r: 4 },
                  activeDot: { r: 6 },
                }
              ],
              height: 300,
            },
            {
              id: 'tu-awsp-gender-completion',
              title: 'Completion by Gender',
              description: 'Tracking gender component of completion',
              type: 'line',
              dataKey: 'tuAwspGenderCompletion',
              xAxis: {
                dataKey: 'quarter',
                type: 'category',
              },
              yAxis: {
                dataKey: 'participants',
                label: 'Participants',
              },
              series: [
                {
                  dataKey: 'participants',
                  name: 'Participants',
                  stroke: '#82ca9d',
                  dot: { r: 4 },
                  activeDot: { r: 6 },
                }
              ],
              height: 300,
            }
          ]
        }
      }
    ],
  },
  'inTulsa - ASWP': {
    id: 'inTulsa-aswp-dashboard',
    name: 'ASWP Performance Dashboard',
    description: 'Key metrics for tracking inTulsa ASWP performance',
    canViewExternal: [],
    sectionGroupings: [
      {
        name: "Primary Metrics",
        layout: {
          columns: 1,
          charts: [
            {
              id: 'major-reports-completed-by-quarter',
              title: 'Major Reports Completed',
              description: 'Deliverables: 1 Research Agenda, which includes 5 major projects and required data elements, produced annually in the years 2026-2029 shared with the Workforce Intermediary.',
              type: 'stackedBar',
              dataKey: 'majorReportsCompleted',
              xAxis: {
                dataKey: 'quarter',
                type: 'category',
              },
              yAxis: {
                label: 'Reports',
                domain: ['auto', 'auto'],
                tickFormatter: 'formatCurrencyShortTooltip'
              },
              series: [
                {
                  dataKey: 'reports',
                  name: 'Reports This Quarter',
                  fill: '#82ca9d',
                  stackId: 'reports'
                },
                {
                  dataKey: 'priorReports',
                  name: 'Reports Previous Quarters',
                  fill: '#8884d8',
                  stackId: 'priorReports'
                }
              ],
              legend: {
                verticalAlign: 'bottom',
                align: 'center',
              },
              tooltip: {
                formatter: 'formatCurrencyShortTooltip'
              },
              height: 350,
              dataBoxes: [
                {
                  label: 'Agendas',
                  value: '1',
                  descriptor: 'Major Agenda',
                  tooltip: '1 Research Agenda with 5 major reports annually from 2026-2029',
                  color: '#4CAF50'
                },
                {
                  label: 'Projects',
                  value: '5',
                  descriptor: 'Major Projects',
                  tooltip: '5 major projects annually from 2026-2029',
                  color: '#2196F3'
                },

              ]
            },
            {
              id: 'adhoc-reports-by-quarter',
              title: 'Ad Hoc Reports Completed',
              description: 'Conduct up to 12 ad hoc analyses annually at the direction of the Workforce Intermediary.',
              type: 'stackedBar',
              dataKey: 'adHocReportsCompleted',
              xAxis: {
                dataKey: 'quarter',
                type: 'category',
              },
              yAxis: {
                label: 'Reports',
                domain: ['auto', 'auto'],
                tickFormatter: 'formatCurrencyShortTooltip'
              },
              series: [
                {
                  dataKey: 'reports',
                  name: 'Reports This Quarter',
                  fill: '#82ca9d',
                  stackId: 'reports'
                },
                {
                  dataKey: 'priorReports',
                  name: 'Reports Previous Quarters',
                  fill: '#8884d8',
                  stackId: 'priorReports'
                }
              ],
              legend: {
                verticalAlign: 'bottom',
                align: 'center',
              },
              tooltip: {
                formatter: 'formatCurrencyShortTooltip'
              },
              height: 350,
              dataBoxes: [
                {
                  label: 'Annual Goal',
                  value: '12',
                  descriptor: 'ad hoc analyses annually',
                  tooltip: 'Up to 12 ad hoc analyses annually (approximately 1 per month)',
                  color: '#2196F3'
                },
              ]
            },
            {
              id: 'total-presentation-submissions',
              title: 'Annual Presentation Proposals',
              description: 'Deliverable: Submit a minimum of 1 proposal annually to present LMO development and/or LMO findings.',
              type: 'bar',
              dataKey: 'presentationSubmissions',
              xAxis: {
                dataKey: 'year',
                type: 'category',
              },
              yAxis: {
                label: 'Proposals',
                domain: ['auto', 'auto'],
                tickFormatter: 'formatCurrencyShortTooltip'
              },
              series: [
                {
                  dataKey: 'proposalSubmissions',
                  name: 'Proposal Submissions',
                  fill: '#8884d8',
                  stackId: 'proposalSubmissions'
                }
              ],
              legend: {
                verticalAlign: 'bottom',
                align: 'center',
              },
              tooltip: {
                formatter: 'formatCurrencyShortTooltip'
              },
              height: 350,
              dataBoxes: [
                {
                  label: 'Annual Goal',
                  value: '1',
                  descriptor: 'Annual proposal submissions',
                  tooltip: 'Submit a minimum of 1 proposal annually to present LMO development and/or LMO findings',
                  color: '#2196F3'
                },
              ]
            },
            {
              id: 'presentations-by-quarter',
              title: 'Presentations',
              description: 'Deliverable: Presentation of research findings at the request of the Workforce Intermediary.',
              type: 'stackedBar',
              dataKey: 'presentations',
              xAxis: {
                dataKey: 'quarter',
                type: 'category',
              },
              yAxis: {
                label: 'Reports',
                domain: ['auto', 'auto'],
                tickFormatter: 'formatCurrencyShortTooltip'
              },
              series: [
                {
                  dataKey: 'internal',
                  name: 'Internal Presentations',
                  fill: '#82ca9d',
                  stackId: 'internal'
                },
                {
                  dataKey: 'external',
                  name: 'External Presentations',
                  fill: '#8884d8',
                  stackId: 'external'
                },
                {
                  dataKey: 'conference',
                  name: 'Conference Presentations',
                  fill: '#82a0caff',
                  stackId: 'conference'
                },
              ],
              legend: {
                verticalAlign: 'bottom',
                align: 'center',
              },
              tooltip: {
                formatter: 'formatCurrencyShortTooltip'
              },
              height: 350,
              dataBoxes: []
            },
          ]
        }
      }
    ],
  },
  'inTulsa - CNTRL': {
    id: 'inTulsa-cntrl-dashboard',
    name: 'Control Tower Performance Dashboard',
    description: 'Key metrics for tracking inTulsa Control Tower performance',
    canViewExternal: [],
    sectionGroupings: [
      {
        name: "Primary Metrics",
        layout: {
          columns: 1,
          charts: [
            {
              id: 'annual-reports-completed-by-quarter',
              title: 'Annual Reports Completed',
              description: '',
              type: 'databox',
              dataKey: 'annualReportsCompleted',
              series: [
                {
                  dataKey: 'value',
                  name: 'Annual Reports',
                  fill: '#82ca9d',
                  stackId: 'reports'
                }
              ],
              height: 200,
            },
          ]
        }
      }
    ],
  },
  'Atlas': {
    id: 'atlas-aswp-dashboard',
    name: 'ASWP Performance Dashboard',
    description: 'Key metrics for tracking Atlas ASWP performance',
    canViewExternal: [],
    sectionGroupings: [
      {
        name: "Primary Metrics",
        layout: {
          columns: 1,
          charts: [
            {
              id: 'atlas-awsp-program-participants',
              title: 'Program Participants',
              description: 'Tracking unique program participants.',
              type: 'line',
              dataKey: 'atlasAwspProgramParticipants',
              xAxis: {
                dataKey: 'quarter',
                type: 'category',
              },
              yAxis: {
                dataKey: 'participants',
                label: 'Program Participants',
              },
              series: [
                {
                  dataKey: 'participants',
                  name: 'Program Participants',
                  stroke: '#82ca9d',
                  dot: { r: 4 },
                  activeDot: { r: 6 },
                }
              ],
              height: 300,
              dataBoxes: [
                {
                  label: 'Total Participants',
                  value: '150',
                  descriptor: 'Since Inception',
                  tooltip: '150 Program Participants since inception',
                  color: '#4CAF50'
                },
              ]
            },
          ]
        }
      },
      {
        name: "Secondary Goals",
        layout: {
          columns: 2,
          charts: [
            {
              id: 'tu-awsp-teachers-trained',
              title: 'Teachers Trained',
              description: 'Tracking unique teachers trained in AI',
              type: 'line',
              dataKey: 'tuAwspTeachersTrained',
              xAxis: {
                dataKey: 'quarter',
                type: 'category',
              },
              yAxis: {
                dataKey: 'teachers',
                label: 'Teachers',
              },
              series: [
                {
                  dataKey: 'teachers',
                  name: 'Teachers',
                  stroke: '#82ca9d',
                  dot: { r: 4 },
                  activeDot: { r: 6 },
                }
              ],
              height: 300,
            },
            {
              id: 'tu-awsp-participation-by-race',
              title: 'Participation by Race',
              description: 'Tracking participation in AI programs by race',
              type: 'line',
              dataKey: 'tuAwspParticipationByRace',
              xAxis: {
                dataKey: 'quarter',
                type: 'category',
              },
              yAxis: {
                dataKey: 'participants',
                label: 'Participants',
              },
              series: [
                {
                  dataKey: 'participants',
                  name: 'Participants',
                  stroke: '#82ca9d',
                  dot: { r: 4 },
                  activeDot: { r: 6 },
                }
              ],
              height: 300,
            },
            {
              id: 'tu-awsp-racial-completion',
              title: 'Completion by Race',
              description: 'Tracking racial component of completion',
              type: 'line',
              dataKey: 'tuAwspRacialCompletion',
              xAxis: {
                dataKey: 'quarter',
                type: 'category',
              },
              yAxis: {
                dataKey: 'participants',
                label: 'Participants',
              },
              series: [
                {
                  dataKey: 'participants',
                  name: 'Participants',
                  stroke: '#82ca9d',
                  dot: { r: 4 },
                  activeDot: { r: 6 },
                }
              ],
              height: 300,
            },
            {
              id: 'tu-awsp-gender-participation',
              title: 'Participation by Gender',
              description: 'Tracking gender participation',
              type: 'line',
              dataKey: 'tuAwspGenderParticipation',
              xAxis: {
                dataKey: 'quarter',
                type: 'category',
              },
              yAxis: {
                dataKey: 'participants',
                label: 'Participants',
              },
              series: [
                {
                  dataKey: 'participants',
                  name: 'Participants',
                  stroke: '#82ca9d',
                  dot: { r: 4 },
                  activeDot: { r: 6 },
                }
              ],
              height: 300,
            },
            {
              id: 'tu-awsp-gender-completion',
              title: 'Completion by Gender',
              description: 'Tracking gender component of completion',
              type: 'line',
              dataKey: 'tuAwspGenderCompletion',
              xAxis: {
                dataKey: 'quarter',
                type: 'category',
              },
              yAxis: {
                dataKey: 'participants',
                label: 'Participants',
              },
              series: [
                {
                  dataKey: 'participants',
                  name: 'Participants',
                  stroke: '#82ca9d',
                  dot: { r: 4 },
                  activeDot: { r: 6 },
                }
              ],
              height: 300,
            }
          ]
        }
      }
    ],
  },
  'Black Tech Street': {
    id: 'bts-gace-dashboard',
    name: 'G-ACE Performance Dashboard',
    description: 'Key metrics for tracking G-ACE performance',
    canViewExternal: [],
    sectionGroupings: [
      {
        name: "Primary Goals",
        layout: {
          columns: 1,
          charts: [
            {
              id: 'bts-gace-participants',
              title: 'Participants',
              description: 'Tracking unique program participants.',
              type: 'line',
              dataKey: 'btsGaceParticipants',
              xAxis: {
                dataKey: 'quarter',
                type: 'category',
              },
              yAxis: {
                dataKey: 'participants',
                label: 'Participants',
              },
              series: [
                {
                  dataKey: 'participants',
                  name: 'Participants',
                  stroke: '#82ca9d',
                  dot: { r: 4 },
                  activeDot: { r: 6 },
                }
              ],
              height: 300,
            },
            {
              id: 'bts-gace-participant-race',
              title: 'Participants by Race',
              description: 'Tracking racial demographics of participants.',
              type: 'stackedBar',
              dataKey: 'btsGaceParticipationByRace',
              xAxis: {
                dataKey: 'quarter',
                type: 'category',
              },
              yAxis: {
                domain: ['auto', 'auto'],
                label: 'Race',
              },
              series: [
                {
                  dataKey: 'btsGaceRaceA',
                  name: 'Race A',
                  fill: '#8884d8',
                  stackId: 'race'
                },
                {
                  dataKey: 'btsGaceRaceB',
                  name: 'Race B',
                  fill: '#82ca9d',
                  stackId: 'race'
                }
              ],
              legend: {
                verticalAlign: 'bottom',
                align: 'center',
              },
              height: 350,
            },
            {
              id: 'bts-gace-engagement-events',
              title: 'Community Engagement Events',
              description: 'Attendee count is ≥ 100',
              type: 'bar',
              dataKey: 'btsGaceCommunityEvents',
              xAxis: {
                dataKey: 'quarter',
                type: 'category',
              },
              yAxis: {
                dataKey: 'events',
                label: 'Events',
                domain: [0, 'auto']
              },
              series: [
                {
                  dataKey: 'events',
                  name: 'Events',
                }
              ],
              height: 300,
            },
            {
              id: 'bts-gace-education-training-events',
              title: 'Education and Training Events',
              description: 'Tracking unique education and training events.',
              type: 'stackedBar',
              dataKey: 'btsGaceEducationTrainingEvents',
              xAxis: {
                dataKey: 'quarter',
                type: 'category',
              },
              yAxis: {
                domain: [0, 'auto'],
                label: 'Events',
              },
              series: [
                {
                  dataKey: 'largeEvents',
                  name: 'Events 100+ Attendees',
                  fill: '#8884d8',
                  stackId: 'events'
                },
                {
                  dataKey: 'smallEvents',
                  name: 'Events ≤ 50 Attendees',
                  fill: '#82ca9d',
                  stackId: 'events'
                }
              ],
              legend: {
                verticalAlign: 'bottom',
                align: 'center',
              },
              height: 350,
            },
            {
              id: 'bts-gace-participant-education',
              title: 'Participation Education',
              description: 'Tracking participant education.',
              type: 'multiline',
              dataKey: 'btsGaceParticipantEducation',
              xAxis: {
                dataKey: 'quarter',
                type: 'category',
              },
              yAxis: {
                type: 'number',
                label: 'Number of Individuals',
                domain: ['auto', 'auto'],
                tickCount: 5,
              },
              series: [
                {
                  dataKey: 'cyberRecognized',
                  name: 'Industry recognized certifications in AI & Cybersecurity',
                  stroke: '#8884d8',
                  dot: { r: 4 },
                  activeDot: { r: 6 },
                },
                {
                  dataKey: 'aiFluent',
                  name: 'Individuals who achieved AI fluency',
                  stroke: '#82ca9d',
                  dot: { r: 4 },
                  activeDot: { r: 6 },
                },
                {
                  dataKey: 'digitalExposure',
                  name: 'Individuals who achieved Digital Exposure',
                  stroke: '#ffc658',
                  dot: { r: 4 },
                  activeDot: { r: 6 },
                },
                {
                  dataKey: 'digitalLiteracy',
                  name: 'Individuals who achieved Digital Literacy',
                  stroke: '#8884d8',
                  strokeDasharray: '5 5',
                  dot: false,
                }
              ],
              legend: {
                verticalAlign: 'bottom',
                align: 'center',
                layout: 'horizontal',
                iconType: 'line'
              },
              height: 350,
            },
            {
              id: 'bts-gace-engagements',
              title: 'Engagements',
              description: 'Tracking engagement types.',
              type: 'stackedBar',
              dataKey: 'btsGaceEngagementStack',
              xAxis: {
                dataKey: 'quarter',
                type: 'category',
              },
              yAxis: {
                domain: [0, 'auto'],
                label: 'Events',
              },
              series: [
                {
                  dataKey: 'labResource',
                  name: 'Lab Resource Engagement',
                  fill: '#8884d8',
                  stackId: 'events'
                },
                {
                  dataKey: 'startupFundable',
                  name: 'Startups - Fundable',
                  stackId: 'events'
                },
                {
                  dataKey: 'startupScalable',
                  name: 'Startups - Scalable',
                  stackId: 'events'
                },
                {
                  dataKey: 'productsDeveloped',
                  name: 'Products - Developed',
                  stackId: 'events'
                },
                {
                  dataKey: 'productsCommercialized',
                  name: 'Products - Commercialized',
                  stackId: 'events'
                },
                {
                  dataKey: 'productsScaled',
                  name: 'Products - Scaled',
                  stackId: 'events'
                }
              ],
              legend: {
                verticalAlign: 'bottom',
                align: 'center',
              },
              height: 350,
            },
            {
              id: 'bts-gace-grants',
              title: 'Grants Awarded',
              description: 'Tracking grants awarded',
              type: 'stackedBar',
              dataKey: 'btsGaceGrants',
              xAxis: {
                dataKey: 'quarter',
                type: 'category',
              },
              yAxis: {
                domain: [0, 'auto'],
                label: 'Grant Proposals',
              },
              series: [
                {
                  dataKey: 'grantProposals',
                  name: 'Grant Proposals',
                  stackId: 'grants'
                },
                {
                  dataKey: 'grantsAwarded',
                  name: 'Grants Awarded',
                  stackId: 'grants'
                },
              ],
              legend: {
                verticalAlign: 'bottom',
                align: 'center',
              },
              height: 350,
              dataBoxes: [
                {
                  label: 'Success Ratio',
                  value: '57%',
                  descriptor: 'YTD 2025',
                  tooltip: 'Year to Date Grant Success Ratio for 2025',
                  color: '#4CAF50'
                },
                {
                  label: 'Success Ratio',
                  value: '46%',
                  descriptor: 'Since Inception',
                  tooltip: 'Success Ratio since inception',
                  color: '#2196F3'
                }
              ]
            },
          ]
        }
      }
    ],
  },
  'default': {
    id: 'default-dashboard',
    name: 'Performance Dashboard',
    description: 'Key performance metrics',
    canViewExternal: [],
    sectionGroupings: [
      {
        name: "Goal-based",
        layout: {
          columns: 2,
          charts: [
            {
              id: 'report-status',
              title: 'Report Status',
              description: 'Distribution of report statuses',
              type: 'pie',
              dataKey: 'reportStatus',
              series: [
                {
                  dataKey: 'value',
                  name: 'Status',
                }
              ],
              height: 300,
            },
            {
              id: 'submission-trend',
              title: 'Submission Trend',
              description: 'Report submissions over time',
              type: 'line',
              dataKey: 'submissionTrend',
              xAxis: {
                dataKey: 'date',
                type: 'category',
              },
              yAxis: {
                dataKey: 'submissions',
                label: 'Submissions',
              },
              series: [
                {
                  dataKey: 'submissions',
                  name: 'Submissions',
                  stroke: '#82ca9d',
                }
              ],
              height: 300,
            }
          ]
        }
      }
    ]

  }
};

export const getDashboardConfig = (groupId: string): DashboardConfig => {

  return performanceDashboardConfigs[groupId] || performanceDashboardConfigs['default'];
};

export default performanceDashboardConfigs;